import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { countLabel, currencyLabel, NOT_ESTABLISHED_LABEL } from './commandPresentation';

/**
 * The first screen's claims live here, deliberately apart from DashboardPage.
 *
 * Two reasons. The first is that these components import no radix primitive,
 * so every CLAIM on the dashboard can be rendered in a Jest test and asserted
 * against real markup rather than against a mock. The second is that a claim
 * kept in one place is a claim that can be pinned by one mutation.
 *
 * Nothing here decides a construction fact. Each component is handed a fact
 * the presenter already read, and its whole job is to render ESTABLISHED and
 * NOT ESTABLISHED so they cannot be mistaken for one another.
 */

/**
 * NOT ESTABLISHED is never conveyed by colour alone, and never by an empty
 * space. A blank where a number belongs reads as "still loading" to everyone
 * who has ever used a dashboard.
 */
const NotEstablished = ({ testId }) => (
  <span className="text-muted-foreground italic" data-testid={testId} data-established="false">
    {NOT_ESTABLISHED_LABEL}
  </span>
);

/**
 * One headline number.
 *
 * `fact` is `{established, value}` from the presenter. `kind` selects the
 * label function — a count or a currency — and nothing else varies, so a
 * currency can never silently render through the count path.
 */
export const CommandStat = ({ label, fact, kind = 'count', sub = null, icon = null, testId }) => {
  const established = Boolean(fact && fact.established);
  const text = kind === 'currency' ? currencyLabel(fact) : countLabel(fact);
  return (
    <Card className="stat-card" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground font-medium">{label}</p>
            {established ? (
              <p className="text-3xl font-bold mt-1" data-testid={`${testId}-value`} data-established="true">
                {text}
              </p>
            ) : (
              <p className="text-xl mt-1">
                <NotEstablished testId={`${testId}-value`} />
              </p>
            )}
            {sub ? <div className="text-sm mt-1">{sub}</div> : null}
          </div>
          {icon ? (
            <div className="h-12 w-12 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
              {icon}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

/** A secondary line under a headline number — same establishment rules. */
export const CommandSubStat = ({ fact, kind = 'count', suffix, tone = 'text-muted-foreground', testId }) => {
  const established = Boolean(fact && fact.established);
  if (!established) return <NotEstablished testId={testId} />;
  const text = kind === 'currency' ? currencyLabel(fact) : countLabel(fact);
  return (
    <span className={tone} data-testid={testId} data-established="true">
      {text}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
};

/**
 * A thing OMNΛS cannot say, said out loud.
 *
 * This is not an error state and must not read like one. The distinction an
 * operator needs is between "we asked and it broke" and "this platform does
 * not hold that", and only the second is true here — so no retry control is
 * offered, because there is nothing to retry.
 */
export const AbsenceNotice = ({ title, headline, detail, testId }) => (
  <Card className="stat-card" data-testid={testId}>
    <CardContent className="p-6 space-y-2">
      <p className="text-sm text-muted-foreground font-medium">{title}</p>
      <p className="text-lg font-semibold" data-testid={`${testId}-headline`} data-established="false">
        {headline}
      </p>
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-detail`}>
        {detail}
      </p>
    </CardContent>
  </Card>
);

/**
 * A refused read of the whole summary.
 *
 * MALFORMED IS NOT EMPTY: this is what stands in place of a dashboard full of
 * zeroes when the payload could not be read. Retry IS offered here, because
 * this one genuinely is a failed read.
 */
export const SummaryRefusal = ({ reason, onRetry, testId = 'command-summary-refused' }) => (
  <Card className="stat-card" data-testid={testId}>
    <CardContent className="p-6 space-y-2">
      <p className="text-lg font-semibold">The project summary could not be read</p>
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-reason`}>
        {reason}
      </p>
      <p className="text-sm text-muted-foreground">
        No figures are shown, because showing zero here would state that this
        organisation has no projects, no budget and no work.
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-md border px-3 py-1.5 text-sm"
          data-testid={`${testId}-retry`}
        >
          Try again
        </button>
      ) : null}
    </CardContent>
  </Card>
);

/**
 * A per-project progress reading.
 *
 * An unassessed project gets no bar at all. A bar at zero says the crew has
 * done nothing, which is a claim about people's work that nobody made.
 */
export const ProjectProgress = ({ progress, testId }) => {
  if (!progress || !progress.established) {
    return (
      <div className="text-sm" data-testid={testId}>
        <NotEstablished testId={`${testId}-value`} />
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Complete</span>
        <span className="font-medium" data-testid={`${testId}-value`} data-established="true">
          {progress.label}
        </span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-muted">
        <div
          className="h-2 rounded-full bg-primary"
          style={{ width: `${progress.value}%` }}
          role="presentation"
        />
      </div>
    </div>
  );
};
